import {compareDesc, format} from 'date-fns';

import {
  DashboardMostRecentLabSubmissionFragment,
  LabSubmissionStatusEnum,
  Maybe,
  MostRecentLabSubmissionWithResultsFragment,
} from '#apollo/schema';

import {SetTrackerProps, TileProps} from './SetTracker.types';

/**
 * Returns a boolean indicating if the given labSubmission should be considered
 * "failed".
 */
export const failedProcessing = (
  labSubmission?: Maybe<
    DashboardMostRecentLabSubmissionFragment | MostRecentLabSubmissionWithResultsFragment
  >,
) =>
  labSubmission?.status === LabSubmissionStatusEnum.QcFailed ||
  labSubmission?.status === LabSubmissionStatusEnum.DnaExtraction2ndTryFailed ||
  labSubmission?.status === LabSubmissionStatusEnum.Sequencing2ndTryFailed ||
  labSubmission?.status === LabSubmissionStatusEnum.Canceled ||
  labSubmission?.status === LabSubmissionStatusEnum.ReplacementRequested;

export const getTrackingTilesProps = ({
  labSubmissions = [],
  mostRecentLabSubmission,
}: SetTrackerProps): TileProps => {
  const hasLabSubmissions = !!labSubmissions?.length;
  const mostRecentStatus = mostRecentLabSubmission?.status;

  if (hasLabSubmissions) {
    if (mostRecentStatus === 'KitRegistered') {
      return {
        leftTileProps: {
          mediaSrc: '/image/mailerCardHero.png',
          mediaType: 'image',
          fullWidth: true,
        },
        rightTileProps: {
          title: 'Mail your sample to our lab',
          mainInfo:
            'Follow the instructions in your TallyAge™ test kit to send us your completed test. We’ll let you know when our lab has received your sample.',
          secondaryInfo:
            'If you haven’t received an update after sending your sample and need help, contact us.',
          primaryButton: {
            title: 'Contact Support',
            href: process.env.NEXT_PUBLIC_CONTACT_URL || '',
          },
        },
      };
    } else {
      if (failedProcessing(mostRecentLabSubmission)) {
        return {
          leftTileProps: {
            mediaSrc: '/image/processingFailed.jpg',
            fullWidth: false,
            mediaType: 'image',
          },
          rightTileProps: {
            title: 'We were unable to process your test',
            mainInfo:
              'There was a problem with your sample and we were unable to process your data.',
            primaryButton: {title: 'Register a new test', href: '/register'},
          },
        };
      } else {
        const trackerInfo = progressTrackerLogic(mostRecentLabSubmission);

        return {
          leftTileProps: {
            fullWidth: false,
            mediaType: 'video',
            mediaSrc:
              'https://tallyhealth-public-assets-prod.s3.us-west-2.amazonaws.com/video/dashboard/progress-tracker.mp4',
            primaryText: 'Expect your results in',
            secondaryText: '2-4 wks',
            tertiaryText: "We'll keep you notified with updates.",
          },
          rightTileProps: {
            title: 'We are processing your TallyAge™ Test',
            steps: trackerInfo,
          },
        };
      }
    }
  } else {
    return {
      leftTileProps: {
        mediaSrc: '/image/gettingStartedCardHero.png',
        fullWidth: true,
        mediaType: 'image',
      },
      rightTileProps: {
        title: 'Take your first TallyAge™ Test',
        mainInfo:
          'Get your TallyAge™ - a snapshot of how your body is aging on a cellular level and get personalized tips to improve your health.',
        secondaryButton: {title: 'Register your test', href: '/register'},
        primaryButton: {
          title: 'Order Now',
          href: '/profile?tab=manage',
        },
      },
    };
  }
};

const progressTrackerLogic = (
  mostRecentLabSubmission?: Maybe<DashboardMostRecentLabSubmissionFragment>,
) => {
  const labStatuses = mostRecentLabSubmission?.labStatuses;
  // List of statuses the front-end cares about.
  // Order is important. Will be used in component.
  const PROGRESS_ITEMS = [
    'SampleReceived',
    'SampleAccepted',
    'DNAExtracted',
    'DNASequenced',
    'Processed',
  ] as const;

  // Shorthand for "string value that is present
  // in array of `ProgressItems`"
  type ProgressItemKey = typeof PROGRESS_ITEMS[number];

  // Make our progress items nice and pretty!
  const PROGRESS_ITEM_LABELS: Record<ProgressItemKey, string> = {
    SampleReceived: 'Sample Received',
    SampleAccepted: 'Sample Accepted',
    DNAExtracted: 'DNA Extracted',
    DNASequenced: 'DNA Processed',
    Processed: 'TallyAge™ Calculated',
  };

  // This maps any status values from the API to the front-end.
  // Some statuses are treated as the same.
  // First-time Failure events are mapped to the last previous success state
  // because failed events are attempted a second time automatically.
  // They are technically still "in progress".
  const LAB_STATUS_TO_PROGRESS_ITEM: Partial<Record<LabSubmissionStatusEnum, ProgressItemKey>> = {
    [LabSubmissionStatusEnum.KitReceived]: 'SampleReceived',
    [LabSubmissionStatusEnum.QcPassed]: 'SampleAccepted',
    [LabSubmissionStatusEnum.DnaExtractionAccepted]: 'DNAExtracted',
    [LabSubmissionStatusEnum.DnaExtraction2ndTryAccepted]: 'DNAExtracted',
    [LabSubmissionStatusEnum.DnaExtractionFailed]: 'SampleAccepted',
    [LabSubmissionStatusEnum.SequencingAccepted]: 'DNASequenced',
    [LabSubmissionStatusEnum.Sequencing2ndTryAccepted]: 'DNASequenced',
    [LabSubmissionStatusEnum.SequencingFailed]: 'DNAExtracted',
    [LabSubmissionStatusEnum.DataDelivered]: 'DNASequenced',
    [LabSubmissionStatusEnum.Ready]: 'Processed',
  };

  // Sorts all statuses so most recent is at the start.
  // When we use .find() later, this will always return us the most recent
  // status, even if there are multiple.
  // Finally the list is filtered to only include statuses we care about.
  if (labStatuses) {
    const orderedStatuses = labStatuses.slice().sort((a, b) => {
      return compareDesc(new Date(a.createdAt), new Date(b.createdAt));
    });
    const filteredStatuses = orderedStatuses.filter(
      labStatus => !!LAB_STATUS_TO_PROGRESS_ITEM[labStatus.status],
    );

    // Transform our statuses into integers using the ordering of
    // `ProgressItems`. We can then tell "how far along" the
    // processing state is as a whole.
    const statusIndices = filteredStatuses.map(labStatus => {
      const progressItemType = LAB_STATUS_TO_PROGRESS_ITEM[labStatus.status];

      if (!progressItemType) {
        return -1;
      }

      return PROGRESS_ITEMS.indexOf(progressItemType);
    });

    const mostRecentStatusIndex = Math.max(...statusIndices);

    const progressTrackerItems = PROGRESS_ITEMS.map(progressItem => {
      const status = filteredStatuses.find(
        labStatus => LAB_STATUS_TO_PROGRESS_ITEM[labStatus.status] === progressItem,
      );
      return {
        title: PROGRESS_ITEM_LABELS[progressItem],
        subtitle: status ? format(new Date(status.createdAt), 'MMM dd, yyyy') : undefined,
      };
    });
    return {mostRecentStatusIndex, progressTrackerItems};
  }
};
